/* eslint-disable react-hooks/exhaustive-deps */
import FilePresentIcon from "@mui/icons-material/FilePresent";
import SnippetFolderIcon from "@mui/icons-material/Folder";
import SearchIcon from "@mui/icons-material/Search";
import FileOpenIcon from "@mui/icons-material/UploadFile";
import { InputAdornment, Pagination, TextField, Tooltip } from "@mui/material";
import moment from "moment";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchUploads, resetStateUploads } from "../../app/slices/uploadSlice";
import {
  journalCategories,
  translate,
  uploadTypes,
} from "../../app/utils";
import "./repository.css";

const Repository = () => {
  const dispatch = useDispatch();
  const { list } = useSelector(({ uploads }) => uploads.get);
  const [documents, setDocuments] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);
  const [language] = useState(
    localStorage.getItem("currentLanguage")?.toUpperCase()
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  useEffect(() => {
    dispatch(fetchUploads({ STATUS: "A" }));
    return () => dispatch(resetStateUploads(["get"]));
  }, [dispatch]);

  useEffect(() => {
    if (!list?.length) return;

    const filteredList = list
      .filter((doc) => doc.TYPE !== "eBook")
      .filter((doc) =>
        selectedCategory ? doc.TYPE === selectedCategory.value : true
      )
      .filter((doc) =>
        selectedSubCategory ? doc.CATEGORY === selectedSubCategory : true
      )
      .filter((doc) =>
        searchTerm
          ? doc[`TITLE_${language}`]
              ?.toLowerCase()
              ?.includes(searchTerm?.toLowerCase())
          : true
      );

    setDocuments(filteredList);
  }, [list, selectedCategory, selectedSubCategory, searchTerm]);

  const handleCategoryClick = (category) => {
    setSelectedCategory((prev) => (prev === category ? null : category));
    setSelectedSubCategory(null);
    setCurrentPage(1);
  };

  const paginatedDocuments = documents.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handlePageChange = (event, value) => setCurrentPage(value);

  const renderDocumentList = (documents) => {
    return documents.map((doc) => (
      <div
        key={doc.id}
        className="list-card"
        onClick={() => {
          window.open(doc[`FILE_PATH_${language}`], "_blank");
        }}
      >
        <div className="doc-icon">
          <FileOpenIcon />
        </div>
        <div className="doc-details">
          <span className="doc-title">{doc[`TITLE_${language}`]}</span>
          <span className="doc-date">{doc.CREATED_AT}</span>
        </div>
      </div>
    ));
  };

  const recentDocuments = (list || [])
    .filter((doc) => doc.TYPE !== "eBook")
    .slice(0, 5);

  const renderRecentDocuments = (documents) => {
    return documents.slice(0, 5).map((doc) => (
      <div
        key={doc.id}
        className="doc-card"
        onClick={() => {
          window.open(doc[`FILE_PATH_${language}`], "_blank");
        }}
      >
        <div className="doc-icon">
          <FilePresentIcon />
        </div>
        <Tooltip title={doc[`TITLE_${language}`]}>
          <div className="doc-details">
            <span className="doc-title">{doc[`TITLE_${language}`]}</span>
            <span className="doc-date">
              {(doc.CREATED_AT && doc.CREATED_AT !== "null") 
                ? moment(doc.CREATED_AT).format("Do MMM YYYY, HH:mm")
                : "Unknown"}
            </span>
          </div>
        </Tooltip>
      </div>
    ));
  };

  const countOccurrences = (item) => {
    return list?.reduce(
      (acc, curr) => (curr?.TYPE === item ? acc + 1 : acc),
      0
    );
  };

  return (
    <div className="rep-main">
      <section className="recent-section">
        <h3>{translate("recentDocuments")}</h3>
        {recentDocuments?.length > 0 ? (
          <div className="recent-container">
            {renderRecentDocuments(recentDocuments)}
          </div>
        ) : (
          <p>{translate("selectCategoryDesc")}</p>
        )}
      </section>

      <section className="list-section">
        <h3>{translate("documents")}</h3>

        <div className="rep-categories">
          {uploadTypes
            ?.filter((i) => i.value !== "eBook")
            ?.map((category) => (
              <div
                key={category.id || category.label}
                className={`rep-card ${
                  selectedCategory?.label === category.label ? "selected" : ""
                }`}
                onClick={() => handleCategoryClick(category)}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  <SnippetFolderIcon style={{ fontSize: "3rem" }} />

                  <span style={{ fontWeight: "bold" }}>
                    {list?.length > 0 && countOccurrences(category.value)}
                  </span>
                </div>
                <span style={{ fontSize: ".9rem" }}>{category.label}</span>
              </div>
            ))}
        </div>

        {selectedCategory?.value === "journal" && (
          <div className="rep-sub-categories">
            {journalCategories?.map((subCat) => (
              <div
                key={subCat}
                className={`rep-card ${
                  selectedSubCategory === subCat ? "selected" : ""
                }`}
                style={{ height: "70px" }}
                onClick={() =>
                  setSelectedSubCategory((prev) =>
                    prev === subCat ? null : subCat
                  )
                }
              >
                <span style={{ fontSize: ".9rem" }}>{subCat}</span>
              </div>
            ))}
          </div>
        )}

        {/* Search bar */}
        <TextField
          fullWidth
          variant="outlined"
          placeholder={translate("searchDocuments")}
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setCurrentPage(1);
          }}
          margin="normal"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />

        {/* Document list */}
        {documents?.length > 0 ? (
          renderDocumentList(paginatedDocuments)
        ) : (
          <p>{translate("selectCategoryDesc")}</p>
        )}

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            marginTop: "20px",
          }}
        >
          <Pagination
            count={Math.ceil(documents.length / itemsPerPage)}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
          />
        </div>
      </section>
    </div>
  );
};

export default Repository;
